import { needsPassword } from './credentials'
import { AuthEvent, AuthState, anonymous } from './states'

/**
 * The only place a transition happens.
 *
 * Pure by design: no React, no Firebase, no navigation, no clock. It is handed the outcome of
 * whatever I/O just finished and answers with the next state — which is what makes every
 * path through signing in reachable in a test, including the ones nobody can reproduce on a
 * device.
 *
 * An event that does not apply to the current state returns the state unchanged rather than
 * throwing. Two callers racing — a provider resolving after the person backed out, say —
 * should not crash the app, and the transitions that matter are asserted in the tests.
 */
export function reduce(state: AuthState, event: AuthEvent): AuthState {
  if (event.type === 'signedOut') return anonymous

  // Asking to sign in starts over, from wherever the last attempt stopped. Without this a
  // flow that ended anywhere but `ready` — abandoned on the registration form, say — left a
  // state that answered no further events, and every provider button silently did nothing.
  if (event.type === 'signInRequested') {
    return { status: 'authenticating', provider: event.provider }
  }

  switch (state.status) {
    case 'anonymous':
    case 'failed':
    case 'ready':
      return state

    case 'authenticating':
      if (event.type === 'credentialObtained') {
        return { status: 'exchanging', provider: state.provider }
      }

      if (event.type === 'failed') {
        return { status: 'failed', provider: state.provider, reason: event.reason, message: event.message }
      }

      return state

    case 'exchanging':
      if (event.type === 'sessionEstablished') {
        return { status: 'resolving', provider: state.provider, uid: event.uid, extras: event.extras }
      }

      if (event.type === 'failed') {
        return { status: 'failed', provider: state.provider, reason: event.reason, message: event.message }
      }

      return state

    case 'resolving':
      // A registration can finish while the lookup is still in flight: the signup form
      // creates the profile itself rather than waiting to be told one is missing.
      if (event.type === 'profileCreated') {
        return { status: 'ready', uid: event.uid, viaRegistration: true }
      }

      if (event.type === 'profileResolved') {
        return { status: 'ready', uid: state.uid, viaRegistration: false }
      }

      // The identity is real — the pool issued its token — but this server has never seen it.
      // That is a registration, not a failure, and it is the state the 2026-09-09 report was
      // stuck in with nothing on screen to say so.
      if (event.type === 'profileAbsent') {
        // The details travel with the state. Dropping them here left the signup form with an
        // address field that was empty and disabled at the same time.
        return {
          status: 'registering',
          provider: state.provider,
          prefill: { ...state.extras, uid: state.uid },
          needsPassword: needsPassword(state.provider)
        }
      }

      if (event.type === 'failed') {
        return { status: 'failed', provider: state.provider, reason: event.reason, message: event.message }
      }

      return state

    case 'registering':
      if (event.type === 'profileCreated') {
        return { status: 'ready', uid: event.uid, viaRegistration: true }
      }

      if (event.type === 'profileResolved') {
        return { status: 'ready', uid: state.prefill.uid, viaRegistration: true }
      }

      if (event.type === 'failed') {
        return { status: 'failed', provider: state.provider, reason: event.reason, message: event.message }
      }

      return state

    default:
      return state
  }
}
